import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useSketchStore } from '../../state/useSketchStore';
import { facePlane, type SketchVec3 } from '../../core/sketch/types';
import {
    geometryPools, resolveInference, parseVcbValue, inferScreenAxis,
    SKETCH_SNAPS_OFF, type AxisLock, type InferenceResult,
} from '../../core/sketch/inference';
import { useSnapStore } from '../snapping/SnapManager';
import { useAppStore } from '../../state/store';
import { useSketchToolbarStore } from '../../state/useSketchToolbarStore';
import type { SketchHit } from './sketchToolBus';

/**
 * Shared input plumbing for Sketch tool hooks.
 *
 * Tools receive bus hits (face or plane). This module turns a hit into the
 * tool's working input: the active drawing plane (face plane when the hit
 * is a face, otherwise the sticky last plane or the Z=0 ground), the
 * inference-resolved point (endpoint/midpoint/on-edge/axis), and the VCB
 * committed value on Enter.
 */

export interface SketchToolEnv {
    /** Current document solids + inference pools (memoized on solids change). */
    pools: ReturnType<typeof geometryPools>;
    camera: THREE.Camera;
    domElement: HTMLCanvasElement;
    /** Plane a hit belongs to: face hit → its face's plane; plane hit → the
     *  sticky plane (starts as Z=0 ground and follows the last face used). */
    planeOf: (hit: SketchHit) => THREE.Plane;
    /** Full inference resolution for a hit (pools + recent + axis lock).
     *  `planeOverride` locks a stroke to one plane: every hit kind (face
     *  picks included) re-projects through the camera ray onto it and the
     *  sticky plane stops following the cursor mid-stroke. */
    infer: (hit: SketchHit, recent: THREE.Vector3[], lock: AxisLock, planeOverride?: THREE.Plane) => InferenceResult;
    /** World-space radius of `px` screen pixels at `at` — screen-sized pick
     *  tolerance for loop closure and similar snap-free decisions. */
    worldToleranceAt: (at: THREE.Vector3, px?: number) => number;
    /** VCB: returns the committed number once, clearing the box. */
    consumeVcb: () => number | null;
    /** Axis-lock keyboard state (Left=X, Right=Y, Up/Down=Z; toggle off). */
    axisLock: AxisLock;
}

export function useSketchToolEnv(): SketchToolEnv {
    const solids = useSketchStore((s) => s.solids);
    const looseEdges = useSketchStore((s) => s.edges);
    const tags = useSketchStore((s) => s.tags);
    const { camera, gl } = useThree();
    // Shared OSNAP state (status bar icons / Snaps dialog): the same
    // per-type toggles and master switch that gate 2D/3D/BIM drafting gate
    // the Sketch inference ladder. `types` is replaced only by real toggle
    // writes, so pointermove feedback writes never re-render tools here.
    const snapEnabled = useSnapStore((s) => s.enabled);
    const snapTypes = useSnapStore((s) => s.types);
    const snapAperture = useSnapStore((s) => s.aperture);

    const pools = useMemo(() => geometryPools(solids, looseEdges, tags), [solids, looseEdges, tags]);

    // Sticky plane: the drawing ground. Starts from the custom drawing axes
    // when the Axes tool has re-placed them (null = world Z=0 ground), then
    // follows the last face used.
    const drawingAxes = useSketchStore((s) => s.drawingAxes);
    const stickyPlane = useMemo(() => {
        if (!drawingAxes) return new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
        const xAxis = new THREE.Vector3(...drawingAxes.xAxis);
        const yAxis = new THREE.Vector3(...drawingAxes.yAxis);
        const normal = new THREE.Vector3().crossVectors(xAxis, yAxis);
        if (normal.lengthSq() < 1e-12) return new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
        normal.normalize();
        return new THREE.Plane().setFromNormalAndCoplanarPoint(normal, new THREE.Vector3(...drawingAxes.origin));
    }, [drawingAxes]);

    const planeOf = useCallback((hit: SketchHit): THREE.Plane => {
        if (hit.kind === 'face' && hit.solidId && hit.faceId) {
            const solid = useSketchStore.getState().solids[hit.solidId];
            const face = solid?.faces.find((f) => f.id === hit.faceId);
            if (face) {
                const plane = facePlane(face);
                // Sticky: drawing continues on this plane on empty space.
                // Plane Following OFF: the sticky plane stays on the drawing
                // axes / world ground (AutoCAD-style UCS drafting) — a face
                // is drawn on only through the direct pick that returned it.
                if (useSketchToolbarStore.getState().planeFollowEnabled) {
                    stickyPlane.copy(plane);
                }
                return plane;
            }
        }
        return stickyPlane;
    }, [stickyPlane]);

    const infer = useCallback((hit: SketchHit, recent: THREE.Vector3[], lock: AxisLock, planeOverride?: THREE.Plane): InferenceResult => {
        const plane = planeOverride ?? planeOf(hit);
        // The raw hit point lies on the face plane (face hits) or the Z=0
        // event plane; when the sticky plane differs from the event plane,
        // re-project through the camera ray so drawing follows the sticky
        // plane across empty space. The ray must come from the pointer px of
        // the exact event being processed — a stale page-level tracker can
        // project a point outside the canvas (e.g. the panel's top-left).
        let point = hit.point.clone();
        // Edge hits re-project onto the sticky plane like empty-space hits:
        // the raw point may sit off-plane (a loose edge on another plane),
        // and strokes must stay on the active drawing plane. When the edge
        // lies on the sticky plane (the common case) the projection is a
        // no-op, so exact on-edge points survive. Loose endpoint hits share
        // the contract (strokes start on drawn endpoints constantly).
        // A plane override projects every hit kind (the stroke must stay on
        // its locked plane); without one only plane/edge hits re-project.
        const wantsProjection = planeOverride
            ? Boolean(hit.pointerPx)
            : (hit.kind === 'plane' || hit.kind === 'edge' || hit.kind === 'looseVertex')
                && Boolean(hit.pointerPx);
        if (wantsProjection && hit.pointerPx) {
            const planeZ = -plane.constant / plane.normal.z;
            const isGround = Math.abs(plane.normal.z) > 0.999 && Math.abs(planeZ) < 1e-6;
            if (planeOverride || !isGround) {
                const rect = gl.domElement.getBoundingClientRect();
                const ndc = new THREE.Vector2(
                    ((hit.pointerPx.x - rect.left) / rect.width) * 2 - 1,
                    -((hit.pointerPx.y - rect.top) / rect.height) * 2 + 1
                );
                camera.updateMatrixWorld();
                const raycaster = new THREE.Raycaster();
                raycaster.setFromCamera(ndc, camera);
                const out = new THREE.Vector3();
                if (raycaster.ray.intersectPlane(plane, out)) point = out;
            }
        }
        // resolveInference compares world points projected to canvas-LOCAL px
        // against the pointer: convert the hit's client px into local px here
        // (toScreen uses rect.width/height only, not rect.left/top). Without
        // a native pointer (synthetic hits), project the plane point instead
        // so the aperture comparison stays self-consistent.
        let pointerLocalPx: THREE.Vector2;
        if (hit.pointerPx) {
            const rect = gl.domElement.getBoundingClientRect();
            pointerLocalPx = new THREE.Vector2(hit.pointerPx.x - rect.left, hit.pointerPx.y - rect.top);
        } else {
            pointerLocalPx = toCanvasPx(point, camera, gl.domElement);
        }
        // ORTHO (status bar toggle, shared with 2D/3D/BIM): force-lock the
        // axis the cursor's screen heading matches — the same Sketch
        // direction grab as live inference, with a wider tolerance since
        // this is a forced mode (a 45° heading stays free). A manual arrow
        // lock wins; the first click of a stroke has no reference point, so
        // ORTHO stays inert until one exists. When the heading is ambiguous
        // the dominant world-axis component of the plane delta decides.
        let effectiveLock = lock;
        // Alt held: the orthogonal helpers stand down for this hit — both
        // the ORTHO forced lock below and the live axis grab inside
        // resolveInference (axisSnapSuppressed). Object snaps and the
        // forced lock stay live, so the muting is surgical.
        if (!effectiveLock && !hit.altKey && useAppStore.getState().orthoMode && recent.length > 0) {
            const last = recent[recent.length - 1];
            effectiveLock = inferScreenAxis(camera, gl.domElement, pointerLocalPx, last, recent, Math.PI / 4);
            if (!effectiveLock || effectiveLock === 'lastSegment') {
                effectiveLock = dominantWorldAxis(point.clone().sub(last));
            }
        }
        // SHIFT momentary lock (Sketch): while Shift is held the stroke
        // direction freezes — the same ORTHO axis grab, but STICKY: the
        // axis is captured at the first move of each segment (the anchor
        // being the segment's start point) and cannot drift with the
        // cursor until Shift is released or the segment commits. Alignment
        // inference still captures along the locked line (crossing
        // guides), so approaching another line's end/edge while locked
        // still snaps — the direction just never leaves the lock.
        if (shiftHeldRef.current && recent.length > 0) {
            const last = recent[recent.length - 1];
            if (!shiftLockRef.current || !shiftLockRef.current.anchor.equals(last)) {
                const grabbed = inferScreenAxis(camera, gl.domElement, pointerLocalPx, last, recent, Math.PI / 4);
                shiftLockRef.current = {
                    anchor: last.clone(),
                    lock: !grabbed || grabbed === 'lastSegment'
                        ? dominantWorldAxis(point.clone().sub(last))
                        : grabbed,
                };
            }
            effectiveLock = shiftLockRef.current.lock;
        } else {
            shiftLockRef.current = null;
        }
        return resolveInference({
            planePoint: point,
            pools,
            context: {
                solids,
                guides: useSketchStore.getState().guides,
                camera,
                domElement: gl.domElement,
                // Same aperture as the drafting OSNAP pass.
                aperturePx: snapAperture,
            },
            recent,
            axisLock: effectiveLock,
            pointerPx: pointerLocalPx,
            // Per-type gates from the shared snap store; the master switch
            // off disables every object-snap family while axis/free-plane
            // inference (not an object snap) stays live.
            snapTypes: snapEnabled ? snapTypes : SKETCH_SNAPS_OFF,
            // Tape Measure guide snapping follows the master switch too.
            guideSnapEnabled: snapEnabled,
            // Alt-held modifier: mute the orthogonal axis grab for this hit.
            axisSnapSuppressed: hit.altKey,
        });
    }, [camera, gl, planeOf, pools, solids, snapEnabled, snapTypes, snapAperture]);

    const consumeVcb = useCallback((): number | null => {
        const text = useSketchStore.getState().vcbText;
        const parsed = parseVcbValue(text);
        useSketchStore.getState().setVcbText('');
        return parsed;
    }, []);

    // Screen-sized pick tolerance in world units (shared helper below).
    const worldToleranceAt = useCallback(
        (at: THREE.Vector3, px = 16): number => screenWorldTolerance(camera, gl.domElement, at, px),
        [camera, gl]
    );

    // Axis-lock keyboard state (arrow keys toggle).
    const [axisLock, setAxisLock] = useState<AxisLock>(null);
    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            const map: Record<string, AxisLock> = {
                ArrowLeft: 'x',
                ArrowRight: 'y',
                ArrowUp: 'z',
                ArrowDown: 'z',
            };
            const lock = map[e.key];
            if (!lock) return;
            e.preventDefault();
            setAxisLock((current) => (current === lock ? null : lock));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // SHIFT momentary lock state: the physical hold is tracked globally;
    // the locked axis itself is captured per segment inside infer(), where
    // the cursor heading against the segment anchor is known.
    const shiftHeldRef = useRef(false);
    const shiftLockRef = useRef<{ anchor: THREE.Vector3; lock: AxisLock } | null>(null);
    useEffect(() => {
        const down = (e: KeyboardEvent): void => {
            if (e.key === 'Shift') shiftHeldRef.current = true;
        };
        const up = (e: KeyboardEvent): void => {
            if (e.key !== 'Shift') return;
            shiftHeldRef.current = false;
            shiftLockRef.current = null;
        };
        const clear = (): void => {
            shiftHeldRef.current = false;
            shiftLockRef.current = null;
        };
        window.addEventListener('keydown', down);
        window.addEventListener('keyup', up);
        window.addEventListener('blur', clear);
        return () => {
            window.removeEventListener('keydown', down);
            window.removeEventListener('keyup', up);
            window.removeEventListener('blur', clear);
        };
    }, []);

    return {
        pools,
        camera,
        domElement: gl.domElement,
        planeOf,
        infer,
        worldToleranceAt,
        consumeVcb,
        axisLock,
    };
}

/** Project a world point to canvas-LOCAL px (same space as inference.ts's
 *  toScreen — rect offset excluded). Used only for synthetic hits that carry
 *  no native pointer px. */
function toCanvasPx(p: THREE.Vector3, camera: THREE.Camera, domElement: HTMLCanvasElement): THREE.Vector2 {
    const rect = domElement.getBoundingClientRect();
    const ndc = p.clone().project(camera);
    return new THREE.Vector2(
        ((ndc.x + 1) / 2) * rect.width,
        ((1 - ndc.y) / 2) * rect.height
    );
}

/** The dominant world-axis component of a delta — the fallback when the
 *  screen-heading grab is ambiguous. Shared by the ORTHO force lock and
 *  the Shift momentary lock. */
function dominantWorldAxis(delta: THREE.Vector3): AxisLock {
    const ax = Math.abs(delta.x);
    const ay = Math.abs(delta.y);
    const az = Math.abs(delta.z);
    return ax >= ay && ax >= az ? 'x' : ay >= az ? 'y' : 'z';
}

/** World-space radius of `px` screen pixels at `at`'s depth (perspective)
 *  or across the ortho frustum — screen-sized tolerance for world-space
 *  decisions. Shared by the tool env and the push/pull alignment pass
 *  (SketchModifyTools), whose gesture hook mounts without a tool env. */
export function screenWorldTolerance(
    camera: THREE.Camera,
    domElement: HTMLCanvasElement,
    at: THREE.Vector3,
    px = 16
): number {
    const height = domElement.clientHeight || domElement.height || 1;
    const persp = camera as THREE.PerspectiveCamera;
    if (persp.isPerspectiveCamera) {
        const fov = THREE.MathUtils.degToRad(persp.fov);
        return ((2 * Math.tan(fov / 2) * persp.position.distanceTo(at)) / height) * px;
    }
    const ortho = camera as THREE.OrthographicCamera;
    const zoom = ortho.zoom > 0 ? ortho.zoom : 1;
    return ((ortho.top - ortho.bottom) / (height * zoom)) * px;
}

/** Dedupe/round helper shared by draw tools (loops stay open). */
export function loopFromPoints(points: THREE.Vector3[]): SketchVec3[] {
    return points.map((p) => [p.x, p.y, p.z]);
}
